import React, { useEffect, useState, useRef, useCallback } from "react";
import { FiAlertCircle, FiCheckCircle, FiClock, FiMessageSquare, FiPlus, FiRefreshCw, FiTool, FiX, FiImage, FiVideo, FiFile, FiUpload, FiTrash2, FiDownload, FiChevronLeft, FiChevronRight, FiPaperclip, FiSend } from "react-icons/fi";
import { portalAPI, getFileUrl } from "../../services/api";
import { usePortalAuth } from "../../context/PortalAuthContext";

const isImage = (f) => f?.file_type?.startsWith("image/") || /\.(jpe?g|png|webp|gif)$/i.test(f?.file_name || "");
const isVideo = (f) => f?.file_type?.startsWith("video/") || /\.(mp4|webm|mov)$/i.test(f?.file_name || "");

const STATUS_CONFIG = {
  open:        { cls: "badge-warning",   label: "Ouvert",   Icon: FiClock },
  in_progress: { cls: "badge-info",      label: "En cours", Icon: FiTool },
  completed:   { cls: "badge-success",   label: "Terminé",  Icon: FiCheckCircle },
  closed:      { cls: "badge-secondary", label: "Fermé",    Icon: FiX },
};
const PRIORITY_CONFIG = {
  urgent: { cls: "badge-danger",    label: "Urgente" },
  high:   { cls: "badge-warning",   label: "Haute"   },
  medium: { cls: "badge-info",      label: "Moyenne" },
  low:    { cls: "badge-secondary", label: "Basse"   },
};

const formatBytes = (b) => { if (!b) return ""; if (b < 1024) return `${b} B`; if (b < 1048576) return `${(b/1024).toFixed(0)} KB`; return `${(b/1048576).toFixed(1)} MB`; };
const StatusBadge = ({ status }) => { const c = STATUS_CONFIG[status] || STATUS_CONFIG.open; return <span className={`badge ${c.cls}`} style={{ display:"inline-flex",alignItems:"center",gap:4 }}><c.Icon size={12}/>{c.label}</span>; };
const PriorityBadge = ({ priority }) => { const c = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium; return <span className={`badge ${c.cls}`}>{c.label}</span>; };

const Lightbox = ({ images, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);
  useEffect(() => { const h = (e) => { if (e.key==="Escape") onClose(); if (e.key==="ArrowLeft") prev(); if (e.key==="ArrowRight") next(); }; window.addEventListener("keydown",h); return () => window.removeEventListener("keydown",h); });
  const cur = images[idx];
  return (
    <div onClick={onClose} style={{ position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,0.92)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center" }}>
      <button onClick={onClose} style={{ position:"absolute",top:16,right:20,background:"none",border:"none",color:"#fff",fontSize:28,cursor:"pointer" }}>×</button>
      {images.length > 1 && <>
        <button onClick={(e)=>{e.stopPropagation();prev();}} style={{ position:"absolute",left:16,background:"rgba(255,255,255,0.15)",border:"none",color:"#fff",borderRadius:"50%",width:44,height:44,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}><FiChevronLeft /></button>
        <button onClick={(e)=>{e.stopPropagation();next();}} style={{ position:"absolute",right:16,background:"rgba(255,255,255,0.15)",border:"none",color:"#fff",borderRadius:"50%",width:44,height:44,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}><FiChevronRight /></button>
      </>}
      <img src={getFileUrl(cur.file_url)||cur.file_url} alt={cur.file_name} onClick={(e)=>e.stopPropagation()} style={{ maxWidth:"90vw",maxHeight:"85vh",objectFit:"contain",borderRadius:8 }} />
      <div style={{ color:"rgba(255,255,255,0.7)",marginTop:12,fontSize:13 }}>{cur.file_name} {images.length>1 && `(${idx+1}/${images.length})`}</div>
    </div>
  );
};

const DropZone = ({ files, onChange, maxFiles=6 }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();
  const addFiles = (newFiles) => { const combined=[...files]; for (const f of newFiles) { if (combined.length>=maxFiles) break; if (!combined.find(x=>x.name===f.name&&x.size===f.size)) combined.push(f); } onChange(combined); };
  const onDrop = useCallback((e) => { e.preventDefault(); setDragging(false); addFiles(Array.from(e.dataTransfer.files)); }, [files]); // eslint-disable-line react-hooks/exhaustive-deps
  const removeFile = (i) => onChange(files.filter((_,ix)=>ix!==i));
  return (
    <div>
      <div onDragOver={(e)=>{e.preventDefault();setDragging(true);}} onDragLeave={()=>setDragging(false)} onDrop={onDrop} onClick={()=>inputRef.current?.click()}
        style={{ border:`2px dashed ${dragging?"var(--primary)":"var(--border)"}`,borderRadius:10,padding:"20px 16px",textAlign:"center",cursor:"pointer",background:dragging?"rgba(124,58,237,0.05)":"var(--bg-secondary)",transition:"all 0.2s" }}>
        <FiUpload size={28} style={{ color:dragging?"var(--primary)":"var(--text-secondary)",marginBottom:8 }} />
        <div style={{ fontSize:13,fontWeight:600 }}>Glissez vos fichiers ici</div>
        <div style={{ fontSize:12,color:"var(--text-secondary)",marginTop:4 }}>ou cliquez — Images, Vidéos, PDF — Max {maxFiles} fichiers</div>
        <input ref={inputRef} type="file" multiple accept="image/*,video/*,application/pdf" style={{ display:"none" }} onChange={(e)=>addFiles(Array.from(e.target.files))} />
      </div>
      {files.length > 0 && (
        <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(80px, 1fr))",gap:8,marginTop:10 }}>
          {files.map((file,i) => {
            const isImg = file.type.startsWith("image/");
            const isVid = file.type.startsWith("video/");
            const prevUrl = isImg ? URL.createObjectURL(file) : null;
            return (
              <div key={i} style={{ position:"relative",borderRadius:8,overflow:"hidden",background:"var(--bg-secondary)",border:"1px solid var(--border)",aspectRatio:"1",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column" }}>
                {isImg && prevUrl ? <img src={prevUrl} alt={file.name} style={{ width:"100%",height:"100%",objectFit:"cover" }} />
                : <div style={{ textAlign:"center",padding:6 }}>{isVid?<FiVideo size={20} color="#7c3aed"/>:<FiFile size={20} color="#64748b"/>}<div style={{ fontSize:10,marginTop:3,color:"var(--text-secondary)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:70 }}>{file.name}</div><div style={{ fontSize:10,color:"var(--text-secondary)" }}>{formatBytes(file.size)}</div></div>}
                <button type="button" onClick={(e)=>{e.stopPropagation();removeFile(i);}} style={{ position:"absolute",top:3,right:3,background:"rgba(220,38,38,0.85)",color:"#fff",border:"none",borderRadius:"50%",width:20,height:20,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}><FiX size={10}/></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const MediaGallery = ({ attachments, onDelete, canDelete }) => {
  const [lbIdx, setLbIdx] = useState(null);
  const images = attachments.filter(isImage);
  const videos = attachments.filter(isVideo);
  const docs   = attachments.filter(a=>!isImage(a)&&!isVideo(a));
  if (attachments.length===0) return <div style={{ textAlign:"center",padding:"20px 0",color:"var(--text-secondary)" }}><FiPaperclip size={28} style={{ marginBottom:8,opacity:0.5 }} /><div style={{ fontSize:13 }}>Aucune pièce jointe</div></div>;
  return (
    <div>
      {images.length > 0 && (
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:12,fontWeight:700,color:"var(--text-secondary)",textTransform:"uppercase",letterSpacing:1,marginBottom:8 }}><FiImage size={12} style={{ marginRight:4 }} />Photos ({images.length})</div>
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(80px, 1fr))",gap:8 }}>
            {images.map((att,i) => (
              <div key={att.id} style={{ position:"relative",borderRadius:8,overflow:"hidden",aspectRatio:"1",cursor:"pointer",border:"1px solid var(--border)" }}>
                <img src={getFileUrl(att.file_url)||att.file_url} alt={att.file_name} style={{ width:"100%",height:"100%",objectFit:"cover" }} onClick={()=>setLbIdx(i)} />
                {canDelete && <button type="button" onClick={()=>onDelete&&onDelete(att.id)} style={{ position:"absolute",top:3,right:3,background:"rgba(220,38,38,0.85)",border:"none",borderRadius:4,color:"#fff",width:20,height:20,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}><FiTrash2 size={10}/></button>}
              </div>
            ))}
          </div>
        </div>
      )}
      {videos.length > 0 && (
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:12,fontWeight:700,color:"var(--text-secondary)",textTransform:"uppercase",letterSpacing:1,marginBottom:8 }}><FiVideo size={12} style={{ marginRight:4 }} />Vidéos ({videos.length})</div>
          {videos.map(att => (
            <div key={att.id} style={{ borderRadius:8,overflow:"hidden",border:"1px solid var(--border)",background:"#000",position:"relative",marginBottom:10 }}>
              <video src={getFileUrl(att.file_url)||att.file_url} controls style={{ width:"100%",maxHeight:220,display:"block" }} preload="metadata" />
              {canDelete && <button type="button" onClick={()=>onDelete&&onDelete(att.id)} style={{ position:"absolute",top:8,right:8,background:"rgba(220,38,38,0.85)",border:"none",borderRadius:6,color:"#fff",padding:"4px 8px",cursor:"pointer",display:"flex",alignItems:"center",gap:4,fontSize:12 }}><FiTrash2 size={12}/> Supprimer</button>}
              <div style={{ padding:"6px 10px",background:"var(--bg-secondary)",fontSize:12,color:"var(--text-secondary)" }}>{att.file_name}{att.file_size?` — ${formatBytes(att.file_size)}`:""}</div>
            </div>
          ))}
        </div>
      )}
      {docs.length > 0 && (
        <div>
          <div style={{ fontSize:12,fontWeight:700,color:"var(--text-secondary)",textTransform:"uppercase",letterSpacing:1,marginBottom:8 }}><FiFile size={12} style={{ marginRight:4 }} />Documents ({docs.length})</div>
          {docs.map(att => (
            <div key={att.id} style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:"var(--bg-secondary)",borderRadius:8,border:"1px solid var(--border)",marginBottom:8 }}>
              <FiFile size={20} color="#7c3aed" style={{ flexShrink:0 }} />
              <div style={{ flex:1,overflow:"hidden" }}><div style={{ fontSize:13,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{att.file_name}</div>{att.file_size&&<div style={{ fontSize:11,color:"var(--text-secondary)" }}>{formatBytes(att.file_size)}</div>}</div>
              <a href={getFileUrl(att.file_url)||att.file_url} target="_blank" rel="noreferrer" style={{ color:"var(--primary)" }}><FiDownload size={16}/></a>
              {canDelete && <button type="button" onClick={()=>onDelete&&onDelete(att.id)} style={{ background:"none",border:"none",color:"#ef4444",cursor:"pointer" }}><FiTrash2 size={16}/></button>}
            </div>
          ))}
        </div>
      )}
      {lbIdx !== null && <Lightbox images={images} startIndex={lbIdx} onClose={()=>setLbIdx(null)} />}
    </div>
  );
};

const PortalMaintenancePage = () => {
  const { user } = usePortalAuth();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [myProperties, setMyProperties] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [ticketUploadFiles, setTicketUploadFiles] = useState([]);
  const [showAttachUpload, setShowAttachUpload] = useState(false);
  const [formData, setFormData] = useState({ property_id:"", category_id:"", title:"", description:"", priority:"medium" });
  const isLocataire = user?.role === "locataire";

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tr, cr, pr] = await Promise.all([
        portalAPI.maintenance.listTickets({ limit:50 }),
        portalAPI.maintenance.getCategories(),
        portalAPI.maintenance.getMyProperties(),
      ]);
      setTickets(tr.data||[]);
      setCategories(cr.data||[]);
      setMyProperties(pr.data||[]);
      if (pr.data?.length>0 && !formData.property_id) setFormData(p=>({...p,property_id:pr.data[0].property_id}));
    } catch(e){ console.error(e); } finally { setLoading(false); }
  };
  useEffect(()=>{ fetchData(); },[]);// eslint-disable-line

  const handleCreateTicket = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const res = await portalAPI.maintenance.createTicket(formData);
      const ticketId = res.data?.id;
      if (uploadFiles.length>0 && ticketId) {
        const fd = new FormData();
        uploadFiles.forEach(f=>fd.append("files",f));
        try { await portalAPI.maintenance.uploadAttachment(ticketId,fd); } catch(e){ console.warn(e); }
      }
      setShowCreateModal(false); setUploadFiles([]);
      setFormData({ property_id:myProperties[0]?.property_id||"", category_id:"", title:"", description:"", priority:"medium" });
      fetchData();
    } catch(e){ alert(e.response?.data?.error||"Erreur lors de la création"); }
    finally { setSubmitting(false); }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!selectedTicket||!newComment.trim()) return;
    try {
      await portalAPI.maintenance.addComment(selectedTicket.id,{content:newComment});
      setNewComment("");
      const r = await portalAPI.maintenance.getTicketById(selectedTicket.id);
      setSelectedTicket(r.data);
    } catch(e){ console.error(e); }
  };

  const handleUploadToTicket = async () => {
    if (!selectedTicket||ticketUploadFiles.length===0) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      ticketUploadFiles.forEach(f=>fd.append("files",f));
      await portalAPI.maintenance.uploadAttachment(selectedTicket.id,fd);
      setTicketUploadFiles([]); setShowAttachUpload(false);
      const r = await portalAPI.maintenance.getTicketById(selectedTicket.id);
      setSelectedTicket(r.data);
    } catch(e){ alert(e.response?.data?.error||"Erreur upload"); }
    finally { setSubmitting(false); }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm("Supprimer cette pièce jointe ?")) return;
    try {
      await portalAPI.maintenance.deleteAttachment(selectedTicket.id,attachmentId);
      const r = await portalAPI.maintenance.getTicketById(selectedTicket.id);
      setSelectedTicket(r.data);
    } catch(e){ alert(e.response?.data?.error||"Erreur suppression"); }
  };

  const openTicketDetail = async (ticket) => {
    try { const r=await portalAPI.maintenance.getTicketById(ticket.id); setSelectedTicket(r.data); }
    catch { setSelectedTicket(ticket); }
  };

  if (!isLocataire) return (
    <div className="page-container"><div className="card"><div className="card-body" style={{ textAlign:"center",padding:40 }}><FiAlertCircle size={48} style={{ color:"var(--text-secondary)",marginBottom:16 }}/><h3>Accès réservé aux locataires</h3></div></div></div>
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 style={{ display:"flex",alignItems:"center",gap:10 }}><FiTool size={24} style={{ color:"var(--primary)" }}/> Maintenance</h1>
          <p style={{ color:"var(--text-secondary)",marginTop:4 }}>Gérez vos demandes de maintenance pour vos biens locatifs.</p>
        </div>
        <div style={{ display:"flex",gap:12,flexWrap:"wrap" }}>
          <button className="btn btn-secondary" onClick={fetchData}><FiRefreshCw/> Actualiser</button>
          <button className="btn btn-primary" onClick={()=>setShowCreateModal(true)}><FiPlus/> Nouvelle demande</button>
        </div>
      </div>

      {loading ? (
        <div className="card"><div className="card-body" style={{ textAlign:"center",padding:40 }}><div className="loading-spinner"/><p>Chargement...</p></div></div>
      ) : tickets.length===0 ? (
        <div className="card"><div className="card-body" style={{ textAlign:"center",padding:40 }}>
          <FiTool size={48} style={{ color:"var(--text-secondary)",marginBottom:16 }}/><h3>Aucune demande</h3>
          <p style={{ color:"var(--text-secondary)",marginBottom:20 }}>Vous n'avez pas encore soumis de demande de maintenance.</p>
          <button className="btn btn-primary" onClick={()=>setShowCreateModal(true)}><FiPlus/> Créer une demande</button>
        </div></div>
      ) : (
        <div style={{ display:"grid",gap:12 }}>
          {tickets.map(ticket => {
            const imgAtt = ticket.attachments?.filter(isImage)||[];
            return (
              <div key={ticket.id} className="card" style={{ cursor:"pointer" }} onClick={()=>openTicketDetail(ticket)}>
                <div className="card-body">
                  <div style={{ display:"flex",gap:14,alignItems:"flex-start" }}>
                    {imgAtt[0]
                      ? <img src={getFileUrl(imgAtt[0].file_url)||imgAtt[0].file_url} alt="" style={{ width:72,height:72,borderRadius:8,objectFit:"cover",flexShrink:0,border:"1px solid var(--border)" }}/>
                      : <div style={{ width:72,height:72,borderRadius:8,background:"var(--bg-secondary)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,border:"1px solid var(--border)" }}><FiTool size={28} color="var(--text-secondary)"/></div>
                    }
                    <div style={{ flex:1,minWidth:0 }}>
                      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8,marginBottom:6 }}>
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontWeight:600,fontSize:15,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{ticket.title}</div>
                          <div style={{ fontSize:12,color:"var(--text-secondary)" }}>{ticket.ticket_number} • {ticket.property_title}</div>
                        </div>
                        <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}><PriorityBadge priority={ticket.priority}/><StatusBadge status={ticket.status}/></div>
                      </div>
                      <div style={{ fontSize:13,color:"var(--text-secondary)",marginBottom:8,lineHeight:1.4 }}>{ticket.description?.substring(0,120)}{ticket.description?.length>120?"...":""}</div>
                      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",fontSize:12,color:"var(--text-secondary)",flexWrap:"wrap",gap:6 }}>
                        <div style={{ display:"flex",gap:8,alignItems:"center",flexWrap:"wrap" }}>
                          {ticket.category_name&&<span className="badge badge-secondary">{ticket.category_name}</span>}
                          <span>Le {new Date(ticket.created_at).toLocaleDateString("fr-FR")}</span>
                          {ticket.attachments?.length>0&&<span style={{ display:"inline-flex",alignItems:"center",gap:3,color:"var(--primary)" }}><FiPaperclip size={11}/>{ticket.attachments.length}</span>}
                        </div>
                        {ticket.provider_name&&<span>Prestataire: {ticket.provider_name}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal Création */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={()=>setShowCreateModal(false)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><h3>Nouvelle demande de maintenance</h3><button className="btn btn-icon" onClick={()=>setShowCreateModal(false)}><FiX/></button></div>
            <form onSubmit={handleCreateTicket}>
              <div className="modal-body" style={{ display:"grid",gap:16 }}>
                <div className="responsive-grid-2">
                  <div className="form-group" style={{ margin:0 }}>
                    <label className="form-label">Bien concerné *</label>
                    <select className="form-input" value={formData.property_id} onChange={e=>setFormData({...formData,property_id:e.target.value})} required>
                      <option value="">Sélectionnez un bien</option>
                      {myProperties.map(p=><option key={p.property_id} value={p.property_id}>{p.property_title}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin:0 }}>
                    <label className="form-label">Catégorie</label>
                    <select className="form-input" value={formData.category_id} onChange={e=>setFormData({...formData,category_id:e.target.value})}>
                      <option value="">Aucune catégorie</option>
                      {categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group" style={{ margin:0 }}>
                  <label className="form-label">Titre du problème *</label>
                  <input type="text" className="form-input" value={formData.title} onChange={e=>setFormData({...formData,title:e.target.value})} placeholder="Ex: Fuite eau salle de bain" required/>
                </div>
                <div className="form-group" style={{ margin:0 }}>
                  <label className="form-label">Description détaillée *</label>
                  <textarea className="form-input" rows={3} value={formData.description} onChange={e=>setFormData({...formData,description:e.target.value})} placeholder="Décrivez le problème..." required/>
                </div>
                <div className="form-group" style={{ margin:0 }}>
                  <label className="form-label">Priorité</label>
                  <select className="form-input" value={formData.priority} onChange={e=>setFormData({...formData,priority:e.target.value})}>
                    <option value="low">Basse</option><option value="medium">Moyenne</option><option value="high">Haute</option><option value="urgent">Urgente</option>
                  </select>
                </div>
                <div className="form-group" style={{ margin:0 }}>
                  <label className="form-label" style={{ display:"flex",alignItems:"center",gap:6 }}><FiPaperclip size={14}/> Photos / Vidéos / Documents (optionnel)</label>
                  <DropZone files={uploadFiles} onChange={setUploadFiles} maxFiles={6}/>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowCreateModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting?"Envoi...":"Soumettre la demande"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Détail Ticket */}
      {selectedTicket && (
        <div className="modal-overlay" onClick={()=>setSelectedTicket(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div><h3 style={{ margin:0 }}>{selectedTicket.title}</h3><div style={{ fontSize:12,color:"var(--text-secondary)",marginTop:2 }}>{selectedTicket.ticket_number} • {selectedTicket.property_title}</div></div>
              <button className="btn btn-icon" onClick={()=>setSelectedTicket(null)}><FiX/></button>
            </div>
            <div className="modal-body" style={{ display:"grid",gap:20 }}>
              <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
                <PriorityBadge priority={selectedTicket.priority}/>
                <StatusBadge status={selectedTicket.status}/>
                {selectedTicket.category_name&&<span className="badge badge-secondary">{selectedTicket.category_name}</span>}
              </div>
              <div className="responsive-grid-2">
                <div><div style={{ fontSize:11,fontWeight:700,color:"var(--text-secondary)",textTransform:"uppercase",marginBottom:2 }}>Bien</div><div style={{ fontSize:14 }}>{selectedTicket.property_title}</div></div>
                <div><div style={{ fontSize:11,fontWeight:700,color:"var(--text-secondary)",textTransform:"uppercase",marginBottom:2 }}>Date</div><div style={{ fontSize:14 }}>{new Date(selectedTicket.created_at).toLocaleString("fr-FR")}</div></div>
                {selectedTicket.provider_name&&<div><div style={{ fontSize:11,fontWeight:700,color:"var(--text-secondary)",textTransform:"uppercase",marginBottom:2 }}>Prestataire</div><div style={{ fontSize:14 }}>{selectedTicket.provider_name}</div></div>}
              </div>
              <div>
                <div style={{ fontSize:11,fontWeight:700,color:"var(--text-secondary)",textTransform:"uppercase",marginBottom:6 }}>Description</div>
                <div style={{ fontSize:14,lineHeight:1.6,whiteSpace:"pre-wrap",background:"var(--bg-secondary)",padding:"12px 14px",borderRadius:8,border:"1px solid var(--border)" }}>{selectedTicket.description}</div>
              </div>
              <div style={{ borderTop:"1px solid var(--border)",paddingTop:16 }}>
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8 }}>
                  <h4 style={{ margin:0,display:"flex",alignItems:"center",gap:6 }}>
                    <FiPaperclip/> Pièces jointes
                    {selectedTicket.attachments?.length>0&&<span style={{ background:"var(--primary)",color:"#fff",borderRadius:12,fontSize:11,padding:"1px 7px",fontWeight:700 }}>{selectedTicket.attachments.length}</span>}
                  </h4>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={()=>setShowAttachUpload(v=>!v)} style={{ display:"flex",alignItems:"center",gap:6 }}><FiUpload size={14}/> Ajouter</button>
                </div>
                {showAttachUpload&&(
                  <div style={{ background:"var(--bg-secondary)",borderRadius:10,padding:14,marginBottom:14,border:"1px solid var(--border)" }}>
                    <DropZone files={ticketUploadFiles} onChange={setTicketUploadFiles} maxFiles={5}/>
                    {ticketUploadFiles.length>0&&(
                      <div style={{ display:"flex",gap:10,marginTop:10,flexWrap:"wrap" }}>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={()=>{setTicketUploadFiles([]);setShowAttachUpload(false);}}>Annuler</button>
                        <button type="button" className="btn btn-primary btn-sm" onClick={handleUploadToTicket} disabled={submitting}>{submitting?"Envoi...":`Envoyer (${ticketUploadFiles.length})`}</button>
                      </div>
                    )}
                  </div>
                )}
                <MediaGallery attachments={selectedTicket.attachments||[]} onDelete={handleDeleteAttachment} canDelete/>
              </div>
              <div style={{ borderTop:"1px solid var(--border)",paddingTop:16 }}>
                <h4 style={{ marginBottom:14,display:"flex",alignItems:"center",gap:6 }}>
                  <FiMessageSquare/> Commentaires
                  {selectedTicket.comments?.filter(c=>!c.is_internal).length>0&&<span style={{ background:"var(--primary)",color:"#fff",borderRadius:12,fontSize:11,padding:"1px 7px",fontWeight:700 }}>{selectedTicket.comments.filter(c=>!c.is_internal).length}</span>}
                </h4>
                {selectedTicket.comments?.filter(c=>!c.is_internal).length===0
                  ? <p style={{ color:"var(--text-secondary)",fontStyle:"italic",fontSize:13 }}>Aucun message pour le moment.</p>
                  : <div style={{ display:"grid",gap:10,marginBottom:16 }}>
                      {selectedTicket.comments?.filter(c=>!c.is_internal).map(comment=>(
                        <div key={comment.id} style={{ padding:"10px 14px",background:"var(--bg-secondary)",borderRadius:8,borderLeft:"3px solid var(--primary)" }}>
                          <div style={{ fontSize:12,color:"var(--text-secondary)",marginBottom:4 }}><strong>{comment.first_name} {comment.last_name}</strong> • {new Date(comment.created_at).toLocaleString("fr-FR")}</div>
                          <div style={{ fontSize:14,lineHeight:1.5 }}>{comment.content}</div>
                        </div>
                      ))}
                    </div>
                }
                <form onSubmit={handleAddComment}>
                  <div style={{ display:"flex",gap:10,alignItems:"flex-end" }}>
                    <textarea className="form-input" value={newComment} onChange={e=>setNewComment(e.target.value)} placeholder="Ajouter un commentaire..." rows={2} style={{ flex:1,resize:"vertical" }}/>
                    <button type="submit" className="btn btn-primary" disabled={!newComment.trim()} style={{ flexShrink:0 }}><FiSend/></button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalMaintenancePage;
